import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApi } from '@/shared/api';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import type {
    GetApiUnits200Item,
    PostApiUnitsBody,
    PutApiUnitsIdBody,
    PostApiUnits201,
    PutApiUnitsId200,
    DeleteApiUnitsId200,
} from '@/shared/api/models';

const getApiMessage = (err: any): string | null => {
    const message = err?.response?.data?.message ?? err?.message ?? err?.error?.message ?? err?.data?.message ?? null;
    return typeof message === 'string' ? message : null;
};

export const useGetUnits = () => {
    const api = getApi();
    return useQuery({
        queryKey: ['units'],
        queryFn: () => api.getApiUnits(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
    });
};

export const useCreateUnit = () => {
    const api = getApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: PostApiUnitsBody) => api.postApiUnits(body) as Promise<PostApiUnits201>,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['units'] });
            toast.success(t('POS.ToastSuccUnitCr'));
        },
        onError: (err: any) => {
            const msg = getApiMessage(err);
            toast.error(msg ?? t('POS.ToastErrUnitCr'));
        },
    });
};

export const useUpdateUnit = () => {
    const api = getApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: PutApiUnitsIdBody }) =>
            api.putApiUnitsId(id, body) as Promise<PutApiUnitsId200>,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['units'] });
            toast.success(t('POS.ToastSuccUnitUp'));
        },
        onError: (err: any) => {
            const msg = getApiMessage(err);
            toast.error(msg ?? t('POS.ToastErrUnitUp'));
        },
    });
};

export const useDeleteUnit = () => {
    const api = getApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => api.deleteApiUnitsId(id) as Promise<DeleteApiUnitsId200>,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['units'] });
            toast.success(t('POS.ToastSuccUnitDel'));
        },
        onError: (err: any) => {
            const msg = getApiMessage(err);
            toast.error(msg ?? t('POS.ToastErrUnitDel'));
        },
    });
};

export type UnitsListRow = GetApiUnits200Item;
